<i18n>
{
  "fr": {
    "login-title": "Connexion requise",
    "login-desc": "Veuillez renseigner votre nom d'utilisateur et votre mot de passe pour accéder au portail.",
    "connect": "Se connecter",
    "user-name-label": "Votre nom d'utilisateur",
    "password-label": "Votre mot de passe",
    "required-field": "Ce champs est requis",
    "wrong-credentials": "Utilisateur ou mot de passe incorrect.",
    "not-enough-access": "Vous ne disposez pas d'accès suffisant.",
    "forgot-password": "Mot de passe oublié ?",
    "new-user": "Nouvel utilisateur",
    "sen-url": "https://www.vs.ch/web/sen/systeme-information-eaux-souterraines-strates-vs",
    "sit-text": "Informations sur les eaux souterraines (accès libre)",
    "sit-label": "Géoportail cantonal ",
    "sit-url": "https://sitonline.vs.ch/environnement/eaux_souterraines/fr/"
  },
  "de": {
    "login-title": "Einloggen",
    "login-desc": "Bitte geben Sie Ihren Benutzernamen und Ihr Passwort ein, um auf das Administrationsfenster für Geodatenformulare zuzugreifen.",
    "connect": "Einloggen",
    "user-name-label": "Ihr Benutzername",
    "password-label": "Ihr Passwort",
    "required-field": "Dieses Feld wird benötigt.",
    "wrong-credentials": "Falscher Benutzer oder falsches Passwort.",
    "not-enough-access": "Sie haben keinen ausreichenden Zugriff.",
    "forgot-password": "Passwort vergessen",
    "new-user": "Neue Benutzer",
    "sen-url": "https://www.vs.ch/de/web/sen/informationssystem-grundwasser-strates-vs",
    "sit-text": "Informationen zum Grundwasser (freier Zugang)",
    "sit-label": "Kantonales Geoportal",
    "sit-url": "https://sitonline.vs.ch/environnement/eaux_souterraines/de/"
  }
}
</i18n>

<template>
  <div id="login">
    <div v-if="ie" class="error">
      <p><b>Microsoft Internet Explorer</b></p>
      <p>Vous utilisez Microsoft Internet Explorer pour consulter ce site mais <b>celui-ci n’est pas capable d’afficher correctement son contenu</b>.<br />Veuillez mettre à jour votre navigateur.</p>
    </div>

    <div class="login-form-container">

      <md-card class="connection-form">
        <md-card-header>
          <div class="md-title">{{ $t('login-title') }} <md-icon :class="keyColorClass">vpn_key</md-icon></div>
        </md-card-header>

        <md-card-content>
          <p class="login-desc">{{ $t('login-desc') }}</p>

          <form v-on:keyup.enter="send">
            <md-field :class="getValidationClass('login')">
              <label>{{ $t('user-name-label') }}</label>
              <md-input ref="loginInput" :disabled="processing" v-model="login"></md-input>
              <span class="md-error" v-if="!$v.login.required">{{ $t('required-field') }}</span>
            </md-field>
            <md-field :class="getValidationClass('password')">
              <label>{{ $t('password-label') }}</label>
              <md-input :disabled="processing" v-model="password" type="password"></md-input>
              <span class="md-error" v-if="!$v.password.required">{{ $t('required-field') }}</span>
            </md-field>
          </form>

          <span class="fail-text" v-if="!!fail">{{ fail }}</span>

          <router-link to="/forgot-password">{{ $t('forgot-password') }}</router-link>
          <br />
          <a :href="$t('sen-url')" target="_blank">{{ $t('new-user') }}</a>
          <br />
          <span>{{ $t('sit-text') }}</span>: <a :href="$t('sit-url')">{{ $t('sit-label') }}</a>
        </md-card-content>

        <md-card-actions>
          <md-button class="md-raised md-primary" :disabled="processing" @click="send">{{ $t('connect') }}</md-button>
          <md-menu md-direction="bottom-end">
            <md-button md-menu-trigger>{{$t($root.$i18n.locale)}}</md-button>

            <md-menu-content class="md-layout-item md-size-10">
              <md-menu-item @click="$root.$i18n.locale = 'fr'">{{ $t('fr') }}</md-menu-item>
              <md-menu-item @click="$root.$i18n.locale = 'de'">{{ $t('de') }}</md-menu-item>
            </md-menu-content>
          </md-menu>
        </md-card-actions>

      </md-card>

    </div>

    <Snackbar ref="snackbar" />

  </div>
</template>

<script>

import UAParser from 'ua-parser-js'
import { validationMixin } from 'vuelidate'
import {
  required
} from 'vuelidate/lib/validators'

import Auth from '@/utils/auth'
import AccessToken from '@/utils/accessToken'

import Snackbar from '@/components/common/snackbar'

const useragent = new UAParser()

export default {
  name: 'Login',
  components: {
    Snackbar
  },
  mixins: [validationMixin],
  validations: {
    login: {
      required
    },
    password: {
      required
    }
  },
  data () {
    return {
      login: '',
      password: '',
      processing: false,
      fail: '',
      redirectTo: 'home'
    }
  },
  computed: {
    browser () {
      return useragent.getBrowser()
    },
    ie () {
      return this.browser.name === 'IE'
    },
    edge () {
      return this.browser.name === 'Edge' && parseInt(this.browser.major, 10) < 76
    },
    keyColorClass () {
      if (!this.processing && !this.fail) return 'md-primary'
      if (this.fail !== '') return 'md-accent'
      return ''
    }
  },
  mounted () {
    this.focusLoginField()
    if (typeof this.$route.query.redirectTo !== 'undefined') this.redirectTo = this.$route.query.redirectTo
  },
  methods: {
    getValidationClass (fieldName) {
      const field = this.$v[fieldName]

      if (field) {
        return {
          'md-invalid': field.$invalid && field.$dirty
        }
      }
    },
    isFormValid () {
      this.$v.$touch()
      return !this.$v.$invalid
    },
    focusLoginField () {
      setTimeout(() => {
        this.$refs.loginInput.$el.focus()
      }, 100)
    },
    send () {
      if (this.isFormValid()) {
        this.processing = true
        this.fail = ''

        // Demande un nouveau JWT
        Auth.login(this.login, this.password).then((token) => {
          AccessToken.set(token)
          const accessToken = AccessToken.get()
          // Contrôle des droits d'accès
          if (accessToken.hasRight('strates_viewer')) {
            this.$router.push({ name: this.redirectTo, query: this.$route.query })
          } else {
            this.fail = this.$t('not-enough-access')
            this.focusLoginField()
          }
        }).catch((e) => {
          this.fail = this.$t('wrong-credentials')
          this.processing = false
          this.handleError(e)
          this.focusLoginField()
        })
      }
    },
    handleError (e) {
      this.$refs.snackbar.showError(e)
    }
  }
}
</script>

<style scoped lang="scss">
  .login-desc {
    margin-top: 0;
  }

  .error {
    background-color: #ff9191
  }

  .fail-text {
    padding-top: 15px;
    color: #ff1744;
    display: block;
  }

  .login-form-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: calc(100vh - 120px);
  }

  .connection-form {
    max-width: 500px;
  }

  .help-go-form {
    padding: 15px;
  }
</style>
